import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  BarChart3,
  FolderOpen,
  RefreshCw,
  Tag,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/utils/cn';
import { FlipkartPageHeader } from '@/components/flipkart/FlipkartPageHeader';
import {
  formatFlipkartBudget,
  summarizeFlipkartCampaigns,
} from '@/components/flipkart/flipkartCampaignSummary';
import {
  flipkartReportRoas,
  formatFlipkartCount,
  formatFlipkartMoney,
  formatFlipkartReportRange,
  formatFlipkartRoas,
  summarizeFlipkartReportRows,
} from '@/components/flipkart/flipkartReportSummary';
import { formatFlipkartReportsSyncToast } from '@/components/flipkart/flipkartSyncMessages';
import {
  flipkartAdsAccountsQueryKey,
  flipkartAdsCampaignsQueryKey,
  flipkartAdsReportsQueryKey,
} from '@/components/FlipkartAdsOauthConnections';
import { flipkartAdsApi } from '@/api/client';
import { describeFlipkartSyncStatus } from '@/api/syncStatus';
import { flipkartAdAccountLabel, workspaceCampaignPath } from '@/api/workspaceScope';

function axiosErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && 'message' in detail) {
      return String((detail as { message?: string }).message || error.message);
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}

export function FlipkartDashboardPage() {
  const { credentialId: credentialIdParam, adAccountId: adAccountIdParam } = useParams<{
    credentialId: string;
    adAccountId: string;
  }>();
  const credentialId = credentialIdParam ? decodeURIComponent(credentialIdParam) : '';
  const adAccountId = adAccountIdParam ? decodeURIComponent(adAccountIdParam) : '';
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const accountsQuery = useQuery({
    queryKey: flipkartAdsAccountsQueryKey(credentialId),
    queryFn: async () => {
      const res = await flipkartAdsApi.listAccounts(credentialId);
      if (!res.success) throw new Error('Failed to load Flipkart ad accounts');
      return res.data.accounts;
    },
    enabled: Boolean(credentialId),
  });

  const campaignsQuery = useQuery({
    queryKey: flipkartAdsCampaignsQueryKey(credentialId, adAccountId),
    queryFn: async () => {
      const res = await flipkartAdsApi.listCampaigns(credentialId, { adAccountId });
      if (!res.success) throw new Error('Failed to load Flipkart campaigns');
      return res.data.campaigns;
    },
    enabled: Boolean(credentialId && adAccountId),
  });

  const reportsQuery = useQuery({
    queryKey: flipkartAdsReportsQueryKey(credentialId, 'campaigns', adAccountId),
    queryFn: async () => {
      const res = await flipkartAdsApi.listReports(credentialId, 'campaigns', { adAccountId });
      if (!res.success) throw new Error('Failed to load Flipkart campaign reports');
      return res.data.rows;
    },
    enabled: Boolean(credentialId && adAccountId),
  });

  const syncReportsMutation = useMutation({
    mutationFn: async () => {
      const res = await flipkartAdsApi.syncReports(credentialId);
      if (!res.success) throw new Error('Failed to sync Flipkart reports');
      return res.data;
    },
    onSuccess: async (data) => {
      showToast(formatFlipkartReportsSyncToast(data));
      await queryClient.invalidateQueries({ queryKey: flipkartAdsReportsQueryKey(credentialId) });
    },
    onError: (error) => {
      showToast(axiosErrorMessage(error), 'error');
    },
  });

  const account = accountsQuery.data?.find((item) => item.ad_account_id === adAccountId);
  const accountName = account ? flipkartAdAccountLabel(account) : adAccountId;
  const syncLabel = accountsQuery.isLoading
    ? 'Loading sync…'
    : accountsQuery.isSuccess
      ? describeFlipkartSyncStatus(account?.synced_at)
      : null;
  const campaigns = useMemo(
    () => (campaignsQuery.data ?? []).filter((campaign) => campaign.ad_account_id === adAccountId),
    [adAccountId, campaignsQuery.data]
  );
  const reportRows = useMemo(
    () => (reportsQuery.data ?? []).filter((row) => row.ad_account_id === adAccountId),
    [adAccountId, reportsQuery.data]
  );
  const summary = useMemo(() => summarizeFlipkartCampaigns(campaigns), [campaigns]);
  const reportTotals = useMemo(() => summarizeFlipkartReportRows(reportRows), [reportRows]);
  const reportRange = formatFlipkartReportRange(reportTotals);
  const workspaceTo = workspaceCampaignPath(
    { platform: 'flipkart', credentialId, accountId: adAccountId },
    'workspace'
  );

  if (!credentialId || !adAccountId) {
    return null;
  }

  const campaignsLoading = campaignsQuery.isLoading;
  const campaignsError = campaignsQuery.isError;
  const reportsLoading = reportsQuery.isLoading;
  const reportsError = reportsQuery.isError;

  return (
    <div className="mx-auto max-w-[1600px] space-y-3.5 px-6 pb-[18px] pt-1">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <FlipkartPageHeader title="Dashboard" accountName={accountName} adAccountId={adAccountId} />
        <Button
          type="button"
          variant="secondary"
          disabled={syncReportsMutation.isPending}
          onClick={() => syncReportsMutation.mutate()}
        >
          {syncReportsMutation.isPending ? (
            <Spinner className="h-3.5 w-3.5" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          )}
          Sync reports
        </Button>
      </div>
      <p className="text-body-sm text-foreground-muted">
        {syncLabel ? <span className="text-foreground">{syncLabel}. </span> : null}
        Campaign totals and stored report metrics for this Flipkart ad account. Open Workspace for
        campaigns, ad groups, keywords, and products.
      </p>

      {campaignsError ? (
        <Card className="p-6 text-center">
          <p className="font-medium text-foreground">Could not load Flipkart campaigns</p>
          <p className="mt-2 text-body-sm text-foreground-muted">
            {(campaignsQuery.error as Error).message}
          </p>
          <Button type="button" className="mt-4" onClick={() => void campaignsQuery.refetch()}>
            Retry
          </Button>
        </Card>
      ) : campaignsLoading ? (
        <div className="flex min-h-[20vh] items-center justify-center">
          <Spinner className="h-8 w-8" />
        </div>
      ) : summary.total === 0 ? (
        <Card className="p-8 text-center text-foreground-muted">
          <p>No campaigns synced yet.</p>
          <Link
            to={workspaceTo}
            className="mt-3 inline-flex text-body-sm font-medium text-brand hover:underline"
          >
            Sync entities in Workspace
          </Link>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <SummaryStatCard
              title="Campaigns"
              value={summary.total.toLocaleString()}
              icon={FolderOpen}
              iconClassName="bg-blue-50 text-blue-600"
            />
            <SummaryStatCard
              title="Daily budget"
              value={formatFlipkartBudget(summary.totalDailyBudget)}
              icon={Wallet}
              iconClassName="bg-emerald-50 text-emerald-600"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <BreakdownCard
              title="By status"
              items={summary.byStatus}
              icon={BarChart3}
              iconClassName="bg-violet-50 text-violet-600"
            />
            <BreakdownCard
              title="By type"
              items={summary.byType}
              icon={Tag}
              iconClassName="bg-orange-50 text-orange-600"
            />
          </div>
        </>
      )}

      <section className="space-y-3">
        <div>
          <h2 className="text-headline-md font-semibold text-foreground">Report metrics</h2>
          <p className="mt-1 text-body-sm text-foreground-muted">
            {reportRange
              ? `Summed campaign report rows from ${reportRange}.`
              : 'Spend, clicks, impressions, and revenue from stored campaign reports.'}
          </p>
        </div>
        {reportsError ? (
          <Card className="p-6 text-center">
            <p className="font-medium text-foreground">Could not load Flipkart reports</p>
            <p className="mt-2 text-body-sm text-foreground-muted">
              {(reportsQuery.error as Error).message}
            </p>
            <Button type="button" className="mt-4" onClick={() => void reportsQuery.refetch()}>
              Retry
            </Button>
          </Card>
        ) : reportsLoading ? (
          <div className="flex justify-center py-10">
            <Spinner className="h-8 w-8" />
          </div>
        ) : reportTotals.rowCount === 0 ? (
          <Card className="flex flex-col items-center justify-center px-6 py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-muted text-foreground-subtle">
              <BarChart3 className="h-6 w-6" strokeWidth={1.5} aria-hidden />
            </div>
            <p className="mt-4 max-w-md text-body-md text-foreground-muted">
              No report metrics yet. Sync reports to pull spend, clicks, impressions, and revenue.
            </p>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <SummaryStatCard title="Spend" value={formatFlipkartMoney(reportTotals.spend)} />
            <SummaryStatCard title="Revenue" value={formatFlipkartMoney(reportTotals.revenue)} />
            <SummaryStatCard title="ROAS" value={formatFlipkartRoas(flipkartReportRoas(reportTotals))} />
            <SummaryStatCard title="Clicks" value={formatFlipkartCount(reportTotals.clicks)} />
            <SummaryStatCard
              title="Impressions"
              value={formatFlipkartCount(reportTotals.impressions)}
            />
            <SummaryStatCard title="Orders" value={formatFlipkartCount(reportTotals.orders)} />
          </div>
        )}
      </section>
    </div>
  );
}

function SummaryStatCard({
  title,
  value,
  icon: Icon,
  iconClassName,
}: {
  title: string;
  value: string;
  icon?: LucideIcon;
  iconClassName?: string;
}) {
  return (
    <Card className="flex items-center gap-4 p-6">
      {Icon && iconClassName ? (
        <div
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
            iconClassName
          )}
        >
          <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
        </div>
      ) : null}
      <div className="min-w-0">
        <p className="text-label-md uppercase tracking-[0.08em] text-foreground-subtle">{title}</p>
        <p className="mt-2 text-headline-lg font-semibold tabular-nums text-foreground">{value}</p>
      </div>
    </Card>
  );
}

function BreakdownCard({
  title,
  items,
  icon: Icon,
  iconClassName,
}: {
  title: string;
  items: Array<{ label: string; count: number }>;
  icon: LucideIcon;
  iconClassName: string;
}) {
  return (
    <Card className="p-6">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
            iconClassName
          )}
        >
          <Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden />
        </div>
        <p className="text-headline-md font-semibold text-foreground">{title}</p>
      </div>
      <ul className="mt-4">
        {items.map((item, index) => (
          <li
            key={item.label}
            className={cn(
              'flex items-center justify-between gap-3 py-3 text-body-md',
              index > 0 && 'border-t border-border'
            )}
          >
            <span className="font-medium text-foreground">{item.label}</span>
            <span className="font-medium text-foreground-muted">{item.count}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
